'use client'

// Wraps a lab image with editorial "specimen" treatment:
//   • inset corner brackets (top-left / top-right / bottom-left / bottom-right)
//   • specimen ID badge in top-left corner
//   • horizontal scale ruler in bottom-right
//   • subtle hover lift (existing CSS class .tech-image takes care of scale)

import Image from 'next/image'

const EARTH    = '#513205'
const EARTH_30 = 'rgba(81,50,5,0.3)'
const EARTH_50 = 'rgba(81,50,5,0.5)'
const CREAM    = '#F5EED9'

interface Props {
  src: string
  alt: string
  caption: string
  specimenId: string   // e.g. "SP-2025-A02"
}

export default function SpecimenFrame({ src, alt, caption, specimenId }: Props) {
  return (
    <figure style={{ margin: 0 }}>
      <div
        className="tech-image"
        style={{
          position: 'relative',
          aspectRatio: '4 / 3',
          border: `1px solid ${EARTH_30}`,
          borderRadius: 4,
          overflow: 'hidden',
          background: 'rgba(255,255,255,0.4)',
          transition: 'transform 350ms ease, box-shadow 350ms ease',
        }}
      >
        <Image
          src={src}
          alt={alt}
          fill
          sizes="(max-width: 1024px) 100vw, 50vw"
          style={{ objectFit: 'cover' }}
        />

        {/* Grain overlay */}
        <div aria-hidden="true" style={{
          position: 'absolute', inset: 0,
          backgroundImage: 'url(/grain.svg)', backgroundSize: '200px 200px',
          opacity: 0.08, mixBlendMode: 'multiply',
          pointerEvents: 'none',
        }} />

        {/* Inset corner brackets */}
        {(['tl','tr','bl','br'] as const).map(c => {
          const d = {
            tl: 'M0 12 L0 0 L12 0',
            tr: 'M8 0 L20 0 L20 12',
            bl: 'M0 8 L0 20 L12 20',
            br: 'M8 20 L20 20 L20 8',
          }[c]
          const pos = {
            tl: { top: 10, left: 10 },
            tr: { top: 10, right: 10 },
            bl: { bottom: 10, left: 10 },
            br: { bottom: 10, right: 10 },
          }[c]
          return (
            <svg
              key={c}
              aria-hidden="true"
              viewBox="0 0 20 20"
              style={{ position: 'absolute', width: 16, height: 16, ...pos, pointerEvents: 'none' }}
            >
              <path d={d} fill="none" stroke={CREAM} strokeWidth={1.5} opacity={0.85} />
            </svg>
          )
        })}

        {/* Specimen ID badge */}
        <div
          style={{
            position: 'absolute',
            top: 14, left: 32,
            padding: '4px 10px',
            background: 'rgba(20,14,4,0.55)',
            border: `1px solid rgba(245,238,217,0.25)`,
            borderRadius: 2,
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            fontFamily: 'var(--font-body)',
            fontSize: 9,
            letterSpacing: '0.32em',
            textTransform: 'uppercase',
            fontWeight: 600,
            color: 'rgba(245,238,217,0.92)',
          }}
        >
          {specimenId}
        </div>

        {/* Scale ruler */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            bottom: 14, right: 32,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '4px 8px',
            background: 'rgba(20,14,4,0.55)',
            border: `1px solid rgba(245,238,217,0.25)`,
            borderRadius: 2,
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
          }}
        >
          <svg width="40" height="6" viewBox="0 0 40 6">
            <line x1="0" y1="3" x2="40" y2="3" stroke="rgba(245,238,217,0.7)" strokeWidth="1" />
            <line x1="0"  y1="0" x2="0"  y2="6" stroke="rgba(245,238,217,0.7)" strokeWidth="1" />
            <line x1="20" y1="1" x2="20" y2="5" stroke="rgba(245,238,217,0.55)" strokeWidth="1" />
            <line x1="40" y1="0" x2="40" y2="6" stroke="rgba(245,238,217,0.7)" strokeWidth="1" />
          </svg>
          <span style={{
            fontFamily: 'var(--font-body)',
            fontSize: 9,
            letterSpacing: '0.24em',
            textTransform: 'uppercase',
            fontWeight: 600,
            color: 'rgba(245,238,217,0.85)',
          }}>
            10 µm
          </span>
        </div>
      </div>
      <figcaption
        style={{
          fontFamily: 'var(--font-body)',
          fontSize: 12,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: EARTH_50,
          fontWeight: 500,
          marginTop: 14,
          lineHeight: 1.5,
          display: 'flex',
          gap: 12,
          alignItems: 'baseline',
          flexWrap: 'wrap',
        }}
      >
        <span style={{
          color: '#9FAF08', fontFamily: 'var(--font-serif)',
          fontStyle: 'italic', fontSize: 14, letterSpacing: '-0.01em',
          fontWeight: 400,
        }}>
          fig.
        </span>
        <span style={{ color: EARTH }}>{caption}</span>
      </figcaption>
    </figure>
  )
}
